// src/components/home/TextIntroBlock.jsx

import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import previewImage from '../../assets/images/preview.png';

const Block = styled.article`
  border:1px solid var(--bdr); border-radius:16px; background:var(--card); box-shadow: var(--shadow);
  padding:16px 16px 8px;
  line-height:1.75;
  h2, h3{ margin:10px 0 8px; }
  p{ margin:0 0 12px; }
  ul{ margin:0 0 12px 18px; }
`;

const VideoContainer = styled.div`
  max-width: 800px;
  margin: 20px auto;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  position: relative;
  background: #000;
  cursor: pointer;
  
  video {
    width: 100%;
    height: auto;
    display: block;
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  z-index: 2;
  transition: opacity 0.3s ease;
  opacity: ${props => props.$isPlaying ? 0 : 1};
  pointer-events: ${props => props.$isPlaying ? 'none' : 'auto'};
`;

const VideoTitle = styled.h3`
  color: #000;
  font-size: clamp(12px, 2.8vw, 18px);
  font-weight: 600;
  text-align: center;
  margin: 0;
  padding: 10px 20px;
  background: var(--gold, #d4af37);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(212, 175, 55, 0.3);
  white-space: nowrap;
  backdrop-filter: blur(8px);
  border: 2px solid rgba(0, 0, 0, 0.1);
  
  @media (max-width: 640px) {
    white-space: normal;
    max-width: 90%;
    font-size: clamp(11px, 2.8vw, 14px);
    padding: 8px 16px;
  }
`;

const PlayButton = styled.button`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: var(--gold, #d4af37);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(212, 175, 55, 0.5);
  backdrop-filter: blur(8px);
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 24px rgba(212, 175, 55, 0.7);
  }
  
  &::after {
    content: '';
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 18px 0 18px 30px;
    border-color: transparent transparent transparent #000;
    margin-left: 6px;
  }
  
  @media (max-width: 640px) {
    width: 70px;
    height: 70px;
    
    &::after {
      border-width: 14px 0 14px 22px;
      margin-left: 4px;
    }
  }
`;

function VideoPlayer({ title }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleToggle = () => {
    if (isPlaying) {
      handlePause();
    } else {
      handlePlay();
    }
  };

  const handleVideoPause = () => {
    setIsPlaying(false);
  };

  return (
    <VideoContainer onClick={handleToggle}>
      <VideoOverlay $isPlaying={isPlaying}>
        <PlayButton onClick={handlePlay} />
        <VideoTitle>{title}</VideoTitle>
      </VideoOverlay>
      <video 
        ref={videoRef}
        preload="metadata"
        onPause={handleVideoPause}
        onEnded={handleVideoPause}
        poster={previewImage}
      >
        <source src="https://www.astorius.ru/wp-content/uploads/2019/05/astoriusoutn.mp4" type="video/mp4" />
      </video>
    </VideoContainer>
  );
}

export default function TextIntroBlock(){
  const { i18n } = useTranslation();

  const currentLang = i18n.language || 'ru';
  const isZh = currentLang === 'zh';
  const isEN = currentLang.startsWith('en');

  // Статичный контент на английском
  if (isEN) {
    return (
      <Block>
        <h2>ASTORIUS — Premium Real Estate Across Russia & the UAE</h2>

        <p>
          We help clients buy, sell and invest in property across <b>Moscow</b>, <b>Moscow Region</b>,
          <b> Saint Petersburg</b>, <b>Sochi</b>, <b>Crimea</b> and the <b>UAE</b>. Our team combines
          market analytics with hands-on brokerage to deliver safe transactions, strong capital
          appreciation and rental yields.
        </p>

        <h3>What we do</h3>
        <ul>
          <li><b>Real Estate & Investments:</b> new builds, ready properties, off-market lots, rental income and capital-growth strategies.</li>
          <li><b>Consulting & Legal:</b> due diligence, contract review, support at every stage, resident visa advisory (UAE).</li>
          <li><b>Design & Fit-Out:</b> turnkey interior concepts, renovation, FF&E, developer coordination.</li>
          <li><b>Analytics & Asset Management:</b> market research, project underwriting, rental management, reporting.</li>
        </ul>

        <p>
          Looking for a penthouse in Moscow, a beachfront villa in Dubai or a stable rental
          portfolio? We curate a personal shortlist that matches your brief and budget — including
          opportunities not published online.
        </p>
        
        <VideoPlayer title="Watch the Corporation Video" />
      </Block>
    );
  }

  // Статичный контент на китайском
  if (isZh) {
    return (
      <Block>
        <h2>ASTORIUS — 俄罗斯和阿联酋的高端房地产</h2>

        <p>
          我们帮助客户在<b>莫斯科</b>、<b>莫斯科州</b>、<b>圣彼得堡</b>、<b>索契</b>、<b>克里米亚</b>和<b>阿联酋</b>购买、出售和投资房地产。我们的团队将市场分析与实际经纪业务相结合，提供安全的交易、强劲的资本增值和租金收益。
        </p>

        <h3>我们的服务</h3>
        <ul>
          <li><b>房地产与投资：</b>新建项目、现房、非公开房源、租金收入和资本增长策略。</li>
          <li><b>咨询与法律：</b>尽职调查、合同审查、全程支持、居留签证咨询（阿联酋）。</li>
          <li><b>设计与装修：</b>交钥匙室内设计概念、装修、家具配置、开发商协调。</li>
          <li><b>分析与资产管理：</b>市场研究、项目评估、租赁管理、报告。</li>
        </ul>

        <p>
          寻找莫斯科的顶层公寓、迪拜的海滨别墅或稳定的租赁投资组合？我们将根据您的需求和预算精心挑选个性化清单——包括未公开发布的机会。
        </p>
        
        <VideoPlayer title="观看公司视频" />
      </Block>
    );
  }

  // Статичный контент на русском (по умолчанию)
  return (
    <Block>
      <h2>АСТОРИУС — Премиальная недвижимость в России и за рубежом</h2>

      <p>
        Мы сопровождаем сделки и инвестиции в недвижимости в <b>Москве</b>, <b>Московской области</b>,
        <b> Санкт-Петербурге</b>, <b>Сочи</b>, <b>Крыму</b> и <b>за рубежом</b>. Соединяем аналитику рынка и
        практику брокериджа: делаем сделки безопасными, а стратегию — доходной и прозрачной.
      </p>

      <h3>Наши направления</h3>
      <ul>
        <li><b>Недвижимость и инвестиции:</b> новостройки, готовые объекты, закрытые лоты, стратегии дохода и роста капитала.</li>
        <li><b>Консалтинг и право:</b> due diligence, проверка договоров, сопровождение на всех этапах, консультации по резидентству (ОАЭ).</li>
        <li><b>Дизайн и отделка:</b> концепции интерьеров «под ключ», ремонт, комплектация, работа с застройщиком.</li>
        <li><b>Аналитика и управление:</b> исследование рынка, инвестиционный расчёт, управление арендой, отчётность.</li>
      </ul>

      <p>
        Хотите пентхаус в Москве, виллу у моря в Дубае или стабильный арендный портфель? Подготовим
        персональную подборку под ваш запрос и бюджет — включая возможности, не опубликованные в открытом доступе.
      </p>
      
      <VideoPlayer title="Просмотреть видео о корпорации" />
    </Block>
  );
}
